import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";
import {
  ChevronRight, CheckCircle2, ArrowUpRight, Loader2, Search,
} from "lucide-react";
import { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

const PUBLIC = `${process.env.REACT_APP_BACKEND_URL}/api/public`;

const PIPELINE_STAGES = ["applied", "screening", "tech_interview", "hr_interview", "offer", "hired"];
const STAGE_LABEL = {
  applied: "Application received",
  screening: "Resume screening",
  tech_interview: "Technical interview",
  hr_interview: "HR interview",
  offer: "Offer extended",
  hired: "Hired",
};

function PublicHeader() {
  return (
    <header className="sticky top-0 z-30 backdrop-blur bg-white/85 border-b border-gray-200">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/careers" className="flex items-center gap-3">
          <img src={BRAND.logoUrl} alt="Solemnity" className="h-9 w-auto" />
          <div className="leading-tight hidden sm:block">
            <div className="font-display font-semibold text-[#0F172A] text-sm">{BRAND.fullName}</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400">Careers</div>
          </div>
        </Link>
        <nav className="flex items-center gap-5 text-sm">
          <Link to="/careers" data-testid="status-back-to-jobs" className="text-gray-600 hover:text-[#0F172A] flex items-center gap-1">
            All open roles
          </Link>
          <Link to="/login" className="font-medium text-[#0F172A] hover:underline flex items-center gap-1">
            Employee sign in <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </nav>
      </div>
    </header>
  );
}

function PublicFooter() {
  return (
    <footer className="border-t border-gray-200 mt-20 py-8">
      <div className="max-w-6xl mx-auto px-6 flex items-center justify-between flex-wrap gap-4 text-xs text-gray-500">
        <div>© {new Date().getFullYear()} {BRAND.fullName}. All rights reserved.</div>
        <div>{BRAND.tagline}</div>
      </div>
    </footer>
  );
}

export default function CareersStatus() {
  const [email, setEmail] = useState("");
  const [applicationId, setApplicationId] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResults(null);
    try {
      const body = { email };
      if (applicationId) body.application_id = applicationId;
      const { data } = await axios.post(`${PUBLIC}/status`, body);
      setResults(data.applications);
      if (!data.applications?.length) {
        toast.info("No applications yet");
      }
    } catch (err) {
      const msg = err.response?.status === 404
        ? "No application found for that email. Double-check the address you used while applying."
        : formatApiError(err.response?.data?.detail) || "Lookup failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="careers-status-page">
      <PublicHeader />

      <section className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Track your application</h1>
        <p className="text-sm text-gray-500 mt-1.5 max-w-xl">
          Enter the email you used while applying. We&apos;ll show you the current stage of every application linked to it.
        </p>

        <Card className="hr-card p-6 mt-6">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Application email</Label>
              <Input
                data-testid="status-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Application ID (optional)</Label>
              <Input
                data-testid="status-app-id"
                value={applicationId}
                onChange={(e) => setApplicationId(e.target.value)}
                placeholder="Reference from the success page"
                className="mt-1.5"
              />
            </div>
            <div className="md:col-span-2">
              <Button
                type="submit"
                disabled={loading || !email}
                data-testid="status-submit"
                className="h-11 bg-[#F26B3A] hover:bg-[#d85d2f]"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <span className="flex items-center gap-2">Check status <ChevronRight className="h-4 w-4" /></span>
                )}
              </Button>
            </div>
          </form>
        </Card>

        {error && (
          <Card className="hr-card p-4 mt-4 border-amber-200 bg-amber-50 text-sm text-amber-900" data-testid="status-error">
            {error}
          </Card>
        )}

        {results && results.length > 0 && (
          <div className="mt-8 space-y-6" data-testid="status-results">
            {results.map((app) => {
              const idx = PIPELINE_STAGES.indexOf(app.stage);
              return (
                <Card key={app.id} className="hr-card p-6" data-testid={`status-app-${app.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <div className="font-display text-xl font-semibold text-[#0F172A]">{app.job_title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Applied {new Date(app.applied_at).toLocaleDateString()} · ID <span className="font-mono">{app.id.slice(0, 8)}</span>
                      </div>
                    </div>
                    <StatusBadge status={app.stage} />
                  </div>
                  <ol className="space-y-2">
                    {PIPELINE_STAGES.map((s, i) => {
                      const done = i < idx;
                      const current = i === idx;
                      return (
                        <li key={s} className="flex items-center gap-3">
                          <div className={`h-7 w-7 rounded-full grid place-items-center text-[11px] font-semibold shrink-0 ${
                            done ? "bg-emerald-500 text-white" :
                            current ? "bg-[#1E88E5] text-white ring-4 ring-[#1E88E5]/15" :
                            "bg-gray-100 text-gray-400"
                          }`}>
                            {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                          </div>
                          <div className={`text-sm ${current ? "font-medium text-[#0F172A]" : done ? "text-gray-700" : "text-gray-400"}`}>
                            {STAGE_LABEL[s]}
                          </div>
                          {current && <span className="text-[10px] uppercase tracking-wider text-[#1E88E5] font-semibold ml-2">In progress</span>}
                        </li>
                      );
                    })}
                  </ol>
                  {app.offer_available && (
                    <div className="mt-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-900">
                      An offer letter has been generated. Please check the email we sent to <b>{email}</b>.
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </section>

      <PublicFooter />
    </div>
  );
}

export { PublicHeader, PublicFooter };
